import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// FLG (compound het) + SPINK5 + IL-13 + VDR — the structural barrier defect that dupilumab cannot reach.
// The aha: dupilumab targets the immune response (correctly, and it works). But the 40% that remains
// is explained by two structural barrier defects and two nutritional deficits that the biologic never touches.

export const eczemaAisha: Scenario = {
  slug: 'eczema-aisha',
  series: 'Skin & Inflammation',
  title: "The best treatment helped. Her skin still would not heal.",
  socialHook:
    "Aisha, 29, finally found a treatment that helped her eczema. Dupilumab cut flares by about 60%, but the dryness, itch, and hand/eye flares kept returning. Her results showed what the medication could not fix.",

  meta: {
    description:
      "A fictional case study: Aisha's eczema improved on dupilumab, but 40% of the problem remained. FLG, SPINK5, IL13, VDR, and low vitamin D showed that her immune treatment was working while her skin barrier still needed direct repair.",
  },

  persona: {
    name: 'Aisha',
    age: 29,
    sex: 'Female',
    ancestry: 'British-Somali',
    occupation: 'Pharmacist',
    chiefConcern:
      "Aisha has had eczema since age 3. Over 26 years she tried topical steroids, emollients, dairy-free, egg-free, and gluten-free diets. Eight months ago she started dupilumab, and for the first time something clearly helped. Flares dropped by about 60%. But the background dryness, itch, and flares around her eyes and hands remain. As a pharmacist, she understands the medication. She wants to know why the best treatment left so much behind.",
    familyHistory:
      "Mother: asthma. Brother: hay fever and eczema. Father: psoriasis. Classic atopic triad — strong family signal for type 2 inflammatory predisposition.",
  },

  clinicalPicture: {
    symptoms: [
      "Persistent background skin dryness and itch despite 8 months of dupilumab",
      "Periodic flares around eyes and hands — not fully suppressed by the biologic",
      "Skin feels like it never fully recovers between treatment applications",
    ],
    labs: [
      {
        name: "TEWL (Transepidermal Water Loss)",
        value: "42 g/m²/h",
        refRange: "<15 g/m²/h",
        flag: "high",
      },
      {
        name: "Vitamin D (25-OH)",
        value: "31 nmol/L",
        refRange: ">75 nmol/L",
        flag: "low",
      },
    ],
    medications: [
      "Dupilumab 300mg SC every 2 weeks (started 8 months ago) — IL-4/IL-13 inhibitor",
      "Topical corticosteroids as needed for acute flares",
    ],
    supplements: ["No regular supplements at baseline"],
    lifestyle: [
      "Multiple dietary exclusion trials completed: dairy-free 2 years, egg-free 1 year, gluten-free 6 months — no sustained benefit",
      "Regular emollient use throughout history — standard petroleum-based and urea-based products",
      "Works as a pharmacist — high occupational hand-washing frequency contributes to hand eczema",
      "Non-smoker, no significant alcohol use",
    ],
  },

  genetics: [
    {
      gene: "FLG",
      variant: "rs61816761 + rs558269137 (compound heterozygous, null variants)",
      rsid: "rs61816761 / rs558269137",
      genotype: "Compound heterozygous — two different loss-of-function alleles",
      plainMeaning:
        "Aisha's skin barrier is physically leaky. The protein that helps hold moisture in and keep irritants out is missing from both gene copies.",
      technicalDetail:
        "Filaggrin cross-links keratin filaments in the stratum corneum and breaks down into natural moisturizing factor — the hygroscopic molecules that keep the barrier hydrated. Compound heterozygous null variants abolish filaggrin production entirely. This explains her elevated TEWL (nearly 3× the threshold defining barrier compromise), S. aureus colonization, and downstream immune activation. The barrier defect is structural, not inflammatory — dupilumab cannot restore a protein that is absent.",
    },
    {
      gene: "SPINK5",
      variant: "rs2303067 (reduced function)",
      rsid: "rs2303067",
      genotype: "Reduced serine protease inhibitor activity",
      plainMeaning:
        "Even when her skin tries to repair, a second variant lets enzymes break down the repair too quickly. That helps explain why her skin never feels fully recovered.",
      technicalDetail:
        "SPINK5 encodes LEKTI, a serine protease inhibitor that regulates enzymes responsible for desquamation (skin cell shedding). Reduced function leaves these proteases unregulated, continuously accelerating barrier breakdown independent of FLG status. This is a second structural driver — active on the same barrier the FLG defect already compromises, explaining why recovery between treatments feels incomplete even when inflammation is suppressed.",
    },
    {
      gene: "IL13",
      variant: "rs1800925 (promoter variant, high producer)",
      rsid: "rs1800925",
      genotype: "High IL-13 producer",
      plainMeaning:
        "Dupilumab was a good match for Aisha because she overproduces the inflammatory signal it blocks. That explains why the immune side improved so much.",
      technicalDetail:
        "IL-13 is the central cytokine driving type 2 inflammation in atopic dermatitis. The high-producer promoter variant amplifies IL-13 output in response to allergen and microbial exposure, making dupilumab a precision match and explaining the robust 60% response. IL-13 also directly suppresses FLG expression in keratinocytes — so the immune overactivity and the barrier defect are not independent: they reinforce each other. Dupilumab partially relieves FLG suppression, but the underlying null variants remain.",
    },
    {
      gene: "VDR",
      variant: "rs731236 (Taq1, reduced receptor sensitivity)",
      rsid: "rs731236",
      genotype: "Reduced vitamin D receptor sensitivity",
      plainMeaning:
        "Aisha's vitamin D is very low, and her cells may be less sensitive to it. That leaves her skin-repair genes with too little signal.",
      technicalDetail:
        "Vitamin D directly upregulates FLG, SPINK5, and antimicrobial peptide expression in keratinocytes. The VDR Taq1 variant reduces cellular responsiveness, requiring higher blood levels to achieve equivalent receptor activation. Combined with a 25-OH vitamin D of 12 ng/mL (well below the 30 ng/mL threshold), the downstream signal for barrier repair genes is severely inadequate — a third independent layer compounding the FLG and SPINK5 structural defects.",
    },
  ],

  beats: [
    {
      type: "narrative",
      heading: "The first treatment that helped still left a mystery",
      body: "Aisha has lived with eczema since she was three. She has tried mild, moderate, and potent topical steroids. She has tried countless emollients and long diet exclusions. Nothing lasted. When her dermatologist offered dupilumab, she understood the mechanism because she dispenses it as a pharmacist. She tried it, and for the first time in adulthood, her eczema clearly improved. Flares dropped by about 60%. But her skin never fully calmed. The itch returned. Her hands and eyelids still flared. The question became sharper: what was dupilumab helping, and what was it leaving untouched?",
    },
    {
      type: "narrative",
      heading: "The fire was lower, but the wall was still broken",
      body: "Eczema can be driven by immune overactivity, a weak skin barrier, or both. Dupilumab targets the immune side, and for Aisha it clearly works. But if the outer skin layer is physically leaky, allergens, irritants, and microbes keep getting through. The immune fire gets quieter, but the wall still has gaps. That is the part her remaining symptoms point to: not a failed biologic, but an untreated barrier problem.",
    },
    {
      type: "reveal",
      heading: "Two barrier problems were hiding under the inflammation",
      body: "Aisha carries two different loss-of-function FLG variants, meaning the filaggrin protein that helps build the outer skin layer is absent rather than simply low. Her water-loss test shows the effect: moisture is escaping at nearly three times the threshold for a compromised barrier. SPINK5 adds a second problem. Even when her skin tries to repair, enzymes can break that repair down too quickly. Dupilumab can calm inflammation, but it cannot rebuild a missing structural protein. That is why barrier repair needs to be a central treatment, not an afterthought.",
    },
    {
      type: "reveal",
      heading: "The medicine fit. The repair signal was still low.",
      body: "Aisha's IL-13 result explains why dupilumab helped: it blocks a signal she tends to overproduce. But calming that signal does not erase the FLG and SPINK5 barrier defects. A second issue is vitamin D. Her level is low, and her VDR result suggests her cells may need a stronger vitamin D signal to respond. Vitamin D helps turn on skin-barrier repair genes, including pathways already under strain in her profile. For Aisha, correcting vitamin D is not a generic wellness step. It supports the repair system her skin is trying to use.",
    },
    {
      type: "action",
      heading: "What targets the part dupilumab cannot reach",
      items: [
        "Make ceramide-dominant barrier repair the daily foundation. Use a formula built around ceramides, cholesterol, and free fatty acids in the right ratio, applied twice daily to all skin, not only visible patches.",
        "Optimize vitamin D with her clinician. Because her level is low and her VDR result suggests reduced sensitivity, she may need a clear replacement plan and a repeat level in about 3 months.",
        "Address omega-3 intake. EPA and DHA help supply the lipids used to seal the outer skin layer. Algae-derived DHA or triglyceride-form fish oil are reasonable forms to discuss.",
        "Ask dermatology about dilute bleach baths twice a week if Staph aureus is part of her flare pattern. Reducing that bacterial trigger can help break the cycle of barrier damage and inflammation.",
        "Discuss whether a more selective IL-13 blocker, such as tralokinumab, belongs in the future plan. This is a specialist conversation, not a recommendation to switch on her own.",
      ],
    },
  ],

  extraBeats: [
    {
      type: "riskdial" as const,
      heading: "Why 40% of Aisha's eczema survives dupilumab — and what addresses it",
      caption:
        "Dupilumab targets the immune response, and it works. The remaining burden points to barrier defects and nutrient gaps that the biologic does not repair directly.",
      baselineLabel: "Residual eczema on dupilumab — barrier defect untreated",
      baseline: 40,
      levers: [
        {
          label: "Ceramide-dominant barrier repair (daily, all skin)",
          delta: -16,
          description: "Addresses the FLG + SPINK5 structural defect directly — the physical layer dupilumab cannot repair. Correct molar ratio ceramide emollient twice daily to all skin.",
        },
        {
          label: "Vitamin D optimized to >40 ng/mL",
          delta: -8,
          description: "Restores VDR-mediated upregulation of FLG, SPINK5, and antimicrobial peptide genes in skin. VDR variant means she needs higher blood levels to achieve the same cellular signal.",
        },
        {
          label: "S. aureus management (dilute bleach baths 2x/week)",
          delta: -7,
          description: "Reduces the microbial trigger that directly degrades filaggrin and amplifies IL-13 release — the reinforcing cycle her FLG null and IL-13 high-producer genotypes make her especially vulnerable to.",
        },
        {
          label: "Omega-3 index to >8% (algae DHA or TG-form fish oil)",
          delta: -6,
          description: "Skin barrier lipid synthesis is supply-limited at 3.8%. EPA and DHA are structural precursors to the ceramides and fatty acids that seal the stratum corneum.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: "eczema-aisha",
    label: "Explore your skin and inflammation genetics in Coach",
  },
}
